// Script for re-using the marketplace header and footer in WebFlow.
// It's intended to be used as a "Code Embded", here is how the script is used in WebFlow:

/*
<script src="https://www.vendr.com/assets/external/ExternalMarketplace.js"></script>

<script>
  document.addEventListener("DOMContentLoaded", () => {
    ExternalMarketplace.init({
      footerNode: document.querySelector("footer"),
      headerNode: document.getElementById("embed-header"),
      styleSheetUrl:
        "https://www.vendr.com/assets/external/ExternalMarketplace.css",
      segmentWriteKey: "GUz9AOqQpNEDLM0PpL1pg2uaiQ4Sl5sl",
      clerkPublishableKey: "pk_test_XXX",
      reforgeApiKey:
        "426-Production-P454-E967-FRONTEND-bc23132b-0bd0-4d70-838b-0789aaf19c48",
      platformAppUrl: "https://app.vendr.com",
      algolia: {
        appId: "VYSCARNLSB",
        defaultIndex: "prod_backoffice",
        searchKey: "20d8ebb03c9f93edf1641cd97e5d970b",
      },
      identityV2Route: "https://www.vendr.com/api/identity/v2",
      // Use new Navbar/FooterSection instead of old Header/Footer (also skips SaasHeader banner)
      newLandingPageEnabled: true,
    });

      const header = document.getElementById("external-marketplace-header");
      const pageWrapper = header.closest(".page-wrapper").parentElement;
      pageWrapper.insertBefore(header, pageWrapper.firstChild);
  });
</script>

<div id="embed-header" style="height:80px"></div>
*/

import "~/globals.css";
import "./routes/_marketplace/marketplace.css";

import * as ReactDOM from "react-dom/client";
import { CookieConsent } from "~/components/CookieConsent";
import { Navbar } from "~/components/DesignSystem/Navbar";
import { FooterSection } from "~/components/DesignSystem/sections/FooterSection";
import { External } from "~/components/External";
import { Footer } from "~/components/Footer";
import { Header } from "~/components/Header";
import { RuthCard } from "~/components/RuthCard";
import { SaasHeader } from "~/components/SaasHeader";
import { CookieConsentProvider } from "~/contexts/cookieConsentContext";
import { NavbarVisibilityProvider } from "~/contexts/navbarVisibilityContext";
import * as AuthSchema from "~/routes/_marketplace.api.identity.v2/schema";

const getIdentityRoute = (configuredRoute?: string): string => {
  // If explicitly configured, use that
  if (configuredRoute) {
    // Auto-fix: if running on preview but configured for production, use preview API
    const currentHost = window.location.host;
    if (
      currentHost.includes("preview.vendr-dev.com") &&
      configuredRoute.includes("www.vendr.com")
    ) {
      return configuredRoute.replace("www.vendr.com", "www.preview.vendr-dev.com");
    }
    return configuredRoute;
  }
  return "/api/identity/v2";
};

const lookupAuth = async (route?: string) => {
  try {
    const response = await fetch(getIdentityRoute(route), {
      mode: "cors",
      credentials: "include",
    });

    if (!response.ok) {
      return AuthSchema.defaultResponse;
    }

    const body = await response.json();

    const result = AuthSchema.responseDecoder.safeParse(body);

    if (!result.success) {
      return AuthSchema.defaultResponse;
    }

    return result.data;
  } catch {
    return AuthSchema.defaultResponse;
  }
};

const renderInShadowDOM = (args: {
  target: Element;
  react: React.ReactNode;
  styleSheetUrl: string;
  segmentWriteKey?: string;
  reforgeApiKey?: string;
  clerkPublishableKey?: string | null;
  authPromise: Promise<AuthSchema.Response>;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  stickyToTop?: boolean;
  position?: string;
  zIndex?: string;
  id?: string;
}) => {
  const container = document.createElement("div");

  if (args.id) {
    container.id = args.id;
  }

  if (args.stickyToTop) {
    container.style.position = "sticky";
    container.style.top = "0px";
  }

  if (args.position) {
    container.style.position = args.position;
  }

  if (args.zIndex) {
    container.style.zIndex = args.zIndex;
  }

  args.target.replaceWith(container);

  const shadowRoot = container.attachShadow({ mode: "open" });

  const reactRoot = ReactDOM.createRoot(shadowRoot);

  reactRoot.render(
    <External
      styleSheetUrl={args.styleSheetUrl}
      algolia={args.algolia}
      authPromise={args.authPromise}
      clerkPublishableKey={args.clerkPublishableKey}
      reforgeApiKey={args.reforgeApiKey}
      segmentWriteKey={args.segmentWriteKey}
    >
      <link rel="stylesheet" href={args.styleSheetUrl} />

      <style>
        {`.radix-themes:where([data-is-root-theme='true']) {
          min-height: unset;
          background: none;
        }`}
      </style>

      {args.react}
    </External>,
  );
};

export type InitArgs = {
  bannerNode?: Element;
  headerNode?: Element;
  footerNode?: Element;
  ruthCardNode?: Element;
  cookieConsentNode?: Element;
  styleSheetUrl: string;
  platformAppUrl: string;
  nimbusUrl: string;
  segmentWriteKey?: string;
  reforgeApiKey?: string;
  clerkPublishableKey?: string;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  identityV2Route?: string;
  newLandingPageEnabled?: boolean;
};

const onMobileMenuClicked = () => {
  const banner = document.getElementById("external-marketplace-banner");

  if (banner) {
    const isHidden = banner.style.display === "none";
    banner.style.display = isHidden ? "block" : "none";
  }
};

export const init = (args: InitArgs) => {
  const authPromise = lookupAuth(args.identityV2Route);

  // Auto-enable new landing page on preview environments for testing
  const isPreviewEnvironment = window.location.host.includes("preview.vendr-dev.com");
  const newLandingPageEnabled = args.newLandingPageEnabled ?? isPreviewEnvironment;

  if (args.footerNode) {
    renderInShadowDOM({
      id: "external-marketplace-footer",
      target: args.footerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      authPromise,
      algolia: args.algolia,
      zIndex: "0",
      react: newLandingPageEnabled ? <FooterSection /> : <Footer />,
    });
  }

  if (args.bannerNode && !newLandingPageEnabled) {
    renderInShadowDOM({
      id: "external-marketplace-banner",
      target: args.bannerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      authPromise,
      algolia: args.algolia,
      zIndex: "0",
      react: <SaasHeader />,
    });
  }

  if (args.headerNode) {
    renderInShadowDOM({
      id: "external-marketplace-header",
      target: args.headerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: args.clerkPublishableKey,
      authPromise,
      algolia: args.algolia,
      stickyToTop: true,
      zIndex: "10",
      react: newLandingPageEnabled ? (
        <NavbarVisibilityProvider>
          <Navbar nimbusUrl={args.nimbusUrl} />
        </NavbarVisibilityProvider>
      ) : (
        <Header
          platformAppUrl={args.platformAppUrl}
          channelTargetOrigin={window.document.URL}
          onMobileMenuClicked={onMobileMenuClicked}
          nimbusUrl={args.nimbusUrl}
        />
      ),
    });
  }

  if (args.ruthCardNode) {
    renderInShadowDOM({
      id: "external-marketplace-ruth-card",
      target: args.ruthCardNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      authPromise,
      algolia: args.algolia,
      react: <RuthCard nimbusUrl={args.nimbusUrl} />,
    });
  }

  if (args.cookieConsentNode) {
    renderInShadowDOM({
      id: "external-marketplace-cookie-consent",
      target: args.cookieConsentNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      reforgeApiKey: args.reforgeApiKey,
      clerkPublishableKey: null,
      authPromise,
      algolia: args.algolia,
      position: "fixed",
      zIndex: "9999",
      react: (
        <CookieConsentProvider>
          <CookieConsent />
        </CookieConsentProvider>
      ),
    });
  }
};
